"use client";

import { useAuthStore } from "@/stores/auth-store";
import { useGameStore } from "@/stores/game-store";
import { useUIStore } from "@/stores/ui-store";
import { MessageSquare, Map, Settings, ClipboardList, Users, BookOpen } from "lucide-react";
import { VoiceButton } from "./VoiceButton";
import Link from "next/link";

export function HudBar() {
  const user = useAuthStore((s) => s.user);
  const view = useGameStore((s) => s.view);
  const currentDepartmentId = useGameStore((s) => s.currentDepartmentId);
  const departments = useGameStore((s) => s.departments);
  const surggies = useGameStore((s) => s.surggies);
  const togglePanel = useUIStore((s) => s.togglePanel);
  const toggleMiniMap = useUIStore((s) => s.toggleMiniMap);
  const activePanel = useUIStore((s) => s.activePanel);

  const currentDept = departments.find((d) => d.id === currentDepartmentId);
  const locationName =
    view === "overworld"
      ? "Overworld"
      : currentDept
      ? currentDept.name
      : "Unknown";

  const xpForNext = (user?.level ?? 1) * 200;
  const xpPercent = user ? Math.min(100, (user.xp / xpForNext) * 100) : 0;

  const buttonBase =
    "p-2 rounded transition-colors duration-150 text-[#5c4a30] hover:text-[#1a1a1a] hover:bg-[#b89968]/40 cursor-pointer";
  const buttonActive =
    "p-2 rounded transition-colors duration-150 text-[#1a1a1a] bg-[#c5a83a] border border-[#1a1a1a] cursor-pointer";

  return (
    <div
      className="absolute top-0 left-0 right-0 z-40 flex items-center justify-between py-3 px-6 bg-[#f5ecd6] border-b-2 border-[#1a1a1a]"
      style={{
        boxShadow: "inset 0 1px 0 #fff7e6, inset 0 -1px 0 #5c4a30",
      }}
    >
      {/* Left: User info */}
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded bg-[#c5a83a] flex items-center justify-center border-2 border-[#1a1a1a]">
          <span className="text-[#1a1a1a] font-mono text-sm font-bold">
            {user?.name?.[0]?.toUpperCase() ?? "?"}
          </span>
        </div>
        <div>
          <div className="flex items-center gap-2">
            <span className="text-[#1a1a1a] font-mono text-sm font-bold">
              {user?.name ?? "Player"}
            </span>
            <span className="text-[#c54a4a] font-mono text-xs font-bold">
              Lv.{user?.level ?? 1}
            </span>
          </div>
          {/* XP Bar */}
          <div className="w-32 h-2 bg-[#b89968] rounded-sm overflow-hidden border border-[#1a1a1a]">
            <div
              className="h-full bg-[#c5a83a] transition-all duration-700 ease-out"
              style={{ width: `${xpPercent}%` }}
            />
          </div>
          {user?.territoryName && (
            <p className="text-[#5c4a30] font-mono text-[10px] truncate max-w-[128px]">
              ◈ {user.territoryName}
            </p>
          )}
        </div>
      </div>

      {/* Center: Location + Surggie count */}
      <div className="text-center flex flex-col items-center gap-0.5 justify-center">
        <div className="flex items-center gap-1.5">
          <span className="inline-block w-1.5 h-1.5 rounded-full bg-[#5c7935] animate-pulse" />
          <span className="text-[#5c4a30] font-mono text-xs uppercase tracking-wider">
            {locationName}
          </span>
        </div>
        {surggies.length > 0 && (
          <span className="text-[#8a7a5c] font-mono text-[10px]">
            {surggies.length} {surggies.length === 1 ? "Surggie" : "Surggies"}
          </span>
        )}
      </div>

      {/* Right: Panel buttons */}
      <div className="flex items-center gap-1">
        <Link href="/party" className={buttonBase} title="Party">
          <Users className="w-5 h-5" />
        </Link>
        <Link href="/dex" className={buttonBase} title="Dex">
          <BookOpen className="w-5 h-5" />
        </Link>
        <button
          onClick={() => togglePanel("chat")}
          className={activePanel === "chat" ? buttonActive : buttonBase}
          title="Chat"
        >
          <MessageSquare className="w-5 h-5" />
        </button>
        <VoiceButton />
        <button
          onClick={() => toggleMiniMap()}
          className={buttonBase}
          title="Map"
        >
          <Map className="w-5 h-5" />
        </button>
        <button
          onClick={() => togglePanel("settings")}
          className={activePanel === "settings" ? buttonActive : buttonBase}
          title="Settings"
        >
          <Settings className="w-5 h-5" />
        </button>
        <button
          onClick={() => togglePanel("tasks")}
          className={activePanel === "tasks" ? buttonActive : buttonBase}
          title="Tasks"
        >
          <ClipboardList className="w-5 h-5" />
        </button>
      </div>
    </div>
  );
}
